import { useState, useEffect } from 'react'
import { useUser } from '@/contexts/UserContext'
import { apiRequest } from '@/config/api'

interface JobSkillMatchProps {
  positionSkills: any[]
  positionCertifications?: any[]
  className?: string
}

const JobSkillMatch = ({ 
  positionSkills, 
  positionCertifications = [],
  className = '' 
}: JobSkillMatchProps) => {
  const [candidateSkills, setCandidateSkills] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { state: userState } = useUser()

  // Fetch candidate skills from the working API
  useEffect(() => {
    const fetchSkills = async () => {
      if (!userState.isAuthenticated) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        
        const response = await apiRequest('profile-tabs/me/profile/skills')
        
        if (response.ok) {
          const data = await response.json()
          if (data.success && data.data?.skills) {
            setCandidateSkills(data.data.skills)
          }
        } else {
          throw new Error('Failed to fetch candidate skills')
        }
      } catch (err: any) {
        console.error('Error fetching candidate skills:', err)
        setError(err.message || 'Failed to fetch skills')
      } finally {
        setIsLoading(false)
      }
    }

    fetchSkills()
  }, [userState.isAuthenticated])

  // Don't show if user is not authenticated or skills failed to load
  if (!userState.isAuthenticated || (!candidateSkills.length && !isLoading)) {
    return null
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
        <h3 className="text-sm font-medium text-blue-900 mb-2">
          🎯 Your Skill Match
        </h3>
        <div className="text-xs text-blue-700">Loading your skills...</div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 ${className}`}>
        <h3 className="text-sm font-medium text-yellow-900 mb-2">
          ⚠️ Skill Match Unavailable
        </h3>
        <div className="text-xs text-yellow-700">Unable to load your skills for matching.</div>
      </div>
    )
  }

  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
      <h3 className="text-sm font-medium text-blue-900 mb-2">
        🎯 Your Skill Match
      </h3>
      <div className="text-sm">
        <div className="flex flex-wrap gap-1">
          {positionSkills.slice(0, 3).map((skill, index) => {
            const hasSkill = candidateSkills.some(cs => 
              cs.skill_name?.toLowerCase() === skill.skill_name?.toLowerCase()
            )
            return (
              <span
                key={index}
                className={`px-2 py-1 rounded text-xs ${
                  hasSkill 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {skill.skill_name}
              </span>
            )
          })}
          {positionSkills.length > 3 && (
            <span className="text-xs text-gray-500">
              +{positionSkills.length - 3} more
            </span>
          )}
        </div>
      </div>
      <p className="text-xs text-blue-700 mt-2">
        Based on your {candidateSkills.length} registered skills
      </p>
    </div>
  )
}

export default JobSkillMatch